import { useState } from 'react';
import { updatePassword } from '../lib/updatePassword';

interface FirstLoginPasswordChangeProps {
  onComplete: () => void;
  onSignOut: () => Promise<void>;
}

const FirstLoginPasswordChange: React.FC<FirstLoginPasswordChangeProps> = ({ onComplete, onSignOut }) => {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordRequirements = [
    { label: 'At least 12 characters', met: password.length >= 12 },
    { label: 'An uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'A lowercase letter', met: /[a-z]/.test(password) },
    { label: 'A number', met: /[0-9]/.test(password) },
    { label: 'A special character', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (passwordRequirements.some((requirement) => !requirement.met)) {
      setError('Your password must meet all of the requirements below.');
      return;
    }
    if (password !== confirmation) {
      setError('The passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await updatePassword(password);
      setPassword('');
      setConfirmation('');
      onComplete();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to update your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await onSignOut();
    } catch {
      setError('Unable to sign out. Please try again.');
    }
  };

  return (
    <main className="flex min-h-[calc(100vh-5rem)] items-center justify-center px-4 py-10">
      <section className="w-full max-w-md rounded-xl bg-white p-6 shadow-md sm:p-8">
        <h1 className="mb-2 text-2xl font-semibold text-gray-900">Choose a new password</h1>
        <p className="mb-6 text-sm leading-relaxed text-gray-600">
          For your account security, create a new password before opening the dashboard.
        </p>
        <form onSubmit={(event) => void submit(event)} className="space-y-4">
          <div>
            <label htmlFor="first-login-password" className="block text-sm font-medium text-gray-700">New password</label>
            <div className="relative mt-1">
              <input
                id="first-login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="min-h-[46px] w-full rounded border border-gray-300 px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-mainBlue"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex min-h-[46px] w-11 items-center justify-center text-mainBlue transition-colors hover:text-mainPink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mainBlue"
              >
                {showPassword ? (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A10.8 10.8 0 0112 5c5.2 0 8.6 4.7 9.5 6.2a1.5 1.5 0 010 1.6 14 14 0 01-3.1 3.5M6.2 6.2a14 14 0 00-3.7 5 1.5 1.5 0 000 1.6C3.4 14.3 6.8 19 12 19c1.2 0 2.3-.3 3.3-.7" />
                  </svg>
                ) : (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.4-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.4 6.5-9.5 6.5S2.5 12 2.5 12z" />
                    <circle cx="12" cy="12" r="2.5" />
                  </svg>
                )}
              </button>
            </div>
            <ul className="mt-2 space-y-1 text-xs text-gray-600" aria-label="Password requirements">
              {passwordRequirements.map((requirement) => (
                <li key={requirement.label} className={requirement.met ? 'text-green-700' : undefined}>
                  <span aria-hidden="true">{requirement.met ? '✓' : '•'} </span>{requirement.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label htmlFor="first-login-confirm" className="block text-sm font-medium text-gray-700">Confirm new password</label>
            <div className="relative mt-1">
              <input
                id="first-login-confirm"
                type={showConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                className="min-h-[46px] w-full rounded border border-gray-300 px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-mainBlue"
              />
              <button
                type="button"
                onClick={() => setShowConfirmation((visible) => !visible)}
                aria-label={showConfirmation ? 'Hide password' : 'Show password'}
                aria-pressed={showConfirmation}
                className="absolute inset-y-0 right-0 flex min-h-[46px] w-11 items-center justify-center text-mainBlue transition-colors hover:text-mainPink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mainBlue"
              >
                {showConfirmation ? (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A10.8 10.8 0 0112 5c5.2 0 8.6 4.7 9.5 6.2a1.5 1.5 0 010 1.6 14 14 0 01-3.1 3.5M6.2 6.2a14 14 0 00-3.7 5 1.5 1.5 0 000 1.6C3.4 14.3 6.8 19 12 19c1.2 0 2.3-.3 3.3-.7" />
                  </svg>
                ) : (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.4-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.4 6.5-9.5 6.5S2.5 12 2.5 12z" />
                    <circle cx="12" cy="12" r="2.5" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="min-h-[48px] w-full rounded bg-mainPink px-4 py-3 font-semibold text-white hover:bg-mainBlue disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Updating password…' : 'Set new password'}
          </button>
        </form>
        <button type="button" onClick={() => void signOut()} className="mt-4 min-h-[44px] w-full text-sm font-medium text-mainBlue hover:underline">
          Sign out
        </button>
      </section>
    </main>
  );
};

export default FirstLoginPasswordChange;
